import { Link } from 'react-router-dom';
import { Facebook, Instagram, MapPin, Phone, Mail, MessageCircle, Star } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { translations } from '@/i18n/translations';

export default function Footer() {
  const { lang, t } = useLanguage();
  const sn = translations.footer.serviceNames;

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Officina Martorana</h3>
            <p className="text-gray-300 text-sm leading-relaxed">{t('footer', 'about')}</p>
            <div className="flex space-x-4 mt-6">
              <a href="https://www.facebook.com/officinamartoranacarservice" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-[#0066b2] transition-colors" data-testid="footer-facebook"><Facebook className="w-5 h-5" /></a>
              <a href="https://www.instagram.com/officinamartoranacarservice" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-[#0066b2] transition-colors" data-testid="footer-instagram"><Instagram className="w-5 h-5" /></a>
              <a href="https://wa.me/393939049492" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-[#25D366] transition-colors" data-testid="footer-whatsapp"><MessageCircle className="w-5 h-5" /></a>
              <a href="https://www.google.com/search?sa=X&sca_esv=8e618d8a520a8321&hl=it&tbm=lcl&q=Bosch+Car+Service+-+Officina+Martorana+srl+Recensioni&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDIzsjA0MbWwMDAztrA0MDcwMNjAyPiK0dQpvzg5Q8E5sUghOLWoLDM5VUFXwT8tLTM5My9RwTexqCS_KBHIKi7KUQhKTU7NK87Mz8tcxEqePgBIlzc4iwAAAA&rldimm=12628145880638907000&ved=2ahUKEwiN7ofjrNuQAxVS_7sIHf_6D9cQ9fQKegQIPxAF&biw=1920&bih=857&dpr=1#lkt=LocalPoiReviews&lrd=0x131a46ae606e2869:0xaf402fc58b4b6278,3,,,," target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-[#4285f4] transition-colors" data-testid="footer-google"><Star className="w-5 h-5" /></a>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer', 'quickLinks')}</h3>
            <ul className="space-y-2">
              <li><Link to="/servizi" className="text-gray-300 hover:text-[#0066b2] transition-colors text-sm" data-testid="footer-link-services">{t('nav', 'services')}</Link></li>
              <li><Link to="/prenota" className="text-gray-300 hover:text-[#0066b2] transition-colors text-sm" data-testid="footer-link-booking">{t('footer', 'bookings')}</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-[#0066b2] transition-colors text-sm" data-testid="footer-link-blog">{t('nav', 'tips')}</Link></li>
              <li><Link to="/contatti" className="text-gray-300 hover:text-[#0066b2] transition-colors text-sm" data-testid="footer-link-contact">{t('nav', 'contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer', 'servicesTitle')}</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              {Object.values(sn).map((s, i) => <li key={i}>{s[lang]}</li>)}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">{t('footer', 'contactTitle')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3 text-sm text-gray-300">
                <MapPin className="w-5 h-5 text-[#0066b2] flex-shrink-0 mt-0.5" />
                <span>Via Pompei 7, 92019 Sciacca (AG), Italia</span>
              </li>
              <li className="flex items-center space-x-3 text-sm text-gray-300">
                <Phone className="w-5 h-5 text-[#0066b2] flex-shrink-0" />
                <a href="tel:+390925259520" className="hover:text-[#0066b2] transition-colors" data-testid="footer-phone">0925 25952</a>
              </li>
              <li className="flex items-center space-x-3 text-sm text-gray-300">
                <Phone className="w-5 h-5 text-[#0066b2] flex-shrink-0" />
                <a href="https://wa.me/393939049492" className="hover:text-[#0066b2] transition-colors" data-testid="footer-whatsapp-link">WhatsApp: +39 393 904 9492</a>
              </li>
              <li className="flex items-center space-x-3 text-sm text-gray-300">
                <Mail className="w-5 h-5 text-[#0066b2] flex-shrink-0" />
                <a href="mailto:officinamartorana@gmail.com" className="hover:text-[#0066b2] transition-colors" data-testid="footer-email">officinamartorana@gmail.com</a>
              </li>
            </ul>
            <div className="mt-4">
              <p className="text-xs text-gray-400 mb-2">{t('footer', 'hoursLabel')}</p>
              <p className="text-sm text-gray-300">{t('footer', 'hoursValue')}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-gray-700 mt-12 pt-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Officina Martorana Bosch Car Service. {t('footer', 'rights')}</p>
            <div className="flex items-center gap-4">
              <Link to="/privacy-policy" className="hover:text-[#0066b2] transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link to="/cookie-policy" className="hover:text-[#0066b2] transition-colors" data-testid="footer-cookie-link">
                Cookie Policy
              </Link>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => window.dispatchEvent(new Event('reopen-cookie-banner'))}
                className="hover:text-[#0066b2] transition-colors"
                data-testid="footer-manage-cookies"
              >
                Gestisci preferenze cookie
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
